<template>
  <div class="message" :class="message.role">
    <div class="avatar">
      {{ message.role === 'user' ? '👤' : '🤖' }}
    </div>
    <div class="bubble">
      <!-- 图片消息 -->
      <img
        v-if="message.type === 'image'"
        :src="message.content"
        alt="图片"
        class="msg-image"
        @click="previewImage"
      />

      <!-- 文件消息 -->
      <div v-else-if="message.type === 'file'" class="msg-file">
        <svg viewBox="0 0 24 24" width="24" height="24" fill="#1a9c8e">
          <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z"/>
        </svg>
        <div class="file-info">
          <span class="file-name">{{ message.fileName || message.content }}</span>
          <span v-if="message.fileSize" class="file-size">{{ formatFileSize(message.fileSize) }}</span>
        </div>
      </div>

      <!-- 文本消息（支持 Markdown 渲染） -->
      <div v-else class="msg-text" v-html="renderedContent"></div>

      <!-- 底部操作栏：左操作按钮 + 右时间 -->
      <div class="bubble-footer">
        <div
          v-if="message.role === 'assistant' && message.type !== 'image' && message.type !== 'file'"
          class="message-actions"
        >
          <!-- 复制按钮 -->
          <button
            class="action-btn"
            :class="{ copied: copied }"
            title="复制"
            @click.stop="handleCopy"
          >
            <svg v-if="!copied" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
            <svg v-else viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </button>

          <!-- 重试按钮 -->
          <button
            class="action-btn"
            title="重试"
            @click.stop="handleRetry"
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="23 4 23 10 17 10"></polyline>
              <polyline points="1 20 1 14 7 14"></polyline>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
            </svg>
          </button>

          <!-- 删除按钮 -->
          <button
            class="action-btn"
            title="删除"
            @click.stop="handleDelete"
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"></path>
              <path d="M10 11v6M14 11v6"></path>
              <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path>
            </svg>
          </button>
        </div>
        <span class="msg-time">{{ formatTime(message.timestamp) }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { formatTime, formatFileSize } from '../utils/helpers'

/**
 * 轻量级语法高亮（基础版）
 * 为常见语言的关键字、注释、字符串、方法名上色
 */
function highlightSyntax(code, lang) {
  if (!code) return ''

  // 公共关键字集合
  const commonKeywords = [
    'import', 'package', 'class', 'interface', 'extends', 'implements',
    'public', 'private', 'protected', 'static', 'final', 'abstract',
    'void', 'int', 'long', 'short', 'byte', 'float', 'double', 'char',
    'boolean', 'String', 'Integer', 'Long', 'Double', 'Float',
    'if', 'else', 'for', 'while', 'do', 'switch', 'case', 'break',
    'continue', 'return', 'new', 'try', 'catch', 'finally', 'throw',
    'throws', 'this', 'super', 'true', 'false', 'null', 'typeof',
    'instanceof', 'in', 'of', 'let', 'const', 'var', 'function',
    'async', 'await', 'yield', 'export', 'import', 'from', 'default',
    'as', 'type', 'interface', 'enum', 'struct', 'impl', 'pub', 'mod',
    'use', 'fn', 'mut', 'ref', 'out', 'params', 'object', 'def',
    'lambda', 'None', 'True', 'False', 'self', 'cls', 'with', 'as',
    'raise', 'except', 'pass', 'global', 'nonlocal', 'match', 'case'
  ]

  // 分步骤处理，避免互相干扰
  let result = code

  // 1. 提取字符串，用占位符（避免字符串中的内容被误处理）
  const strings = []
  result = result.replace(/("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|`(?:[^`\\]|\\.)*`)/g, (match) => {
    strings.push(match)
    return '\u0001STR' + (strings.length - 1) + '\u0001'
  })

  // 2. 提取注释（单行 + 多行）
  const comments = []
  // 多行注释 /* ... */
  result = result.replace(/\/\*[\s\S]*?\*\//g, (match) => {
    comments.push(match)
    return '\u0002CMT' + (comments.length - 1) + '\u0002'
  })
  // 单行注释 // ... 和 # ...
  result = result.replace(/(\/\/[^\n]*|#[^\n]*)/g, (match) => {
    comments.push(match)
    return '\u0002CMT' + (comments.length - 1) + '\u0002'
  })

  // 3. 提取方法名调用（防止被关键字处理干扰）
  const methods = []
  result = result.replace(/([a-zA-Z_$][\w$]*)(\s*\()/g, (match, name, paren) => {
    // 排除关键字作为方法名的情况
    if (commonKeywords.includes(name)) return match
    methods.push(name)
    return '\u0003MTD' + (methods.length - 1) + '\u0003' + paren
  })

  // 4. 处理关键字（按单词边界匹配）
  const kwRegex = new RegExp('\\b(' + commonKeywords.map(k => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|') + ')\\b', 'g')
  result = result.replace(kwRegex, '<span class="hl-keyword">$1</span>')

  // 5. 还原方法名并上色
  methods.forEach((name, i) => {
    result = result.replace('\u0003MTD' + i + '\u0003', '<span class="hl-method">' + name + '</span>')
  })

  // 6. 还原注释并上色
  comments.forEach((c, i) => {
    result = result.replace('\u0002CMT' + i + '\u0002', '<span class="hl-comment">' + escapeHtml(c) + '</span>')
  })

  // 7. 还原字符串并上色
  strings.forEach((s, i) => {
    result = result.replace('\u0001STR' + i + '\u0001', '<span class="hl-string">' + escapeHtml(s) + '</span>')
  })

  return result
}

function escapeHtml(s) {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

/**
 * 语言标签（极简深灰胶囊风格，参考 GitHub / VS Code 设计）
 * 使用彩色小色点区分不同语言，整体风格统一简洁
 */
function getLangBadge(lang) {
  const l = (lang || 'text').toLowerCase()
  // 每种语言对应一个彩色小色点
  const dotColors = {
    java: '#f89820',
    javascript: '#f7df1e',
    js: '#f7df1e',
    typescript: '#3178c6',
    ts: '#3178c6',
    python: '#3776ab',
    py: '#3776ab',
    go: '#00add8',
    golang: '#00add8',
    rust: '#dea584',
    rs: '#dea584',
    c: '#a8b9cc',
    'c++': '#f34b7d',
    cpp: '#f34b7d',
    'c#': '#68217a',
    csharp: '#68217a',
    cs: '#68217a',
    html: '#e34f26',
    css: '#1572b6',
    scss: '#cc6699',
    less: '#1d365d',
    json: '#cbcb41',
    xml: '#8c4a00',
    yaml: '#cc2323',
    yml: '#cc2323',
    vue: '#42b883',
    react: '#61dafb',
    jsx: '#61dafb',
    tsx: '#61dafb',
    shell: '#4eaa25',
    bash: '#4eaa25',
    sh: '#4eaa25',
    sql: '#00758f',
    mysql: '#00758f',
    markdown: '#083fa1',
    md: '#083fa1',
    php: '#777bb4',
    ruby: '#cc342d',
    rb: '#cc342d',
    kotlin: '#7f52ff',
    kt: '#7f52ff',
    swift: '#fa7343'
  }
  // 语言显示名称（首字母大写）
  const displayNames = {
    javascript: 'JavaScript',
    js: 'JavaScript',
    typescript: 'TypeScript',
    ts: 'TypeScript',
    python: 'Python',
    py: 'Python',
    golang: 'Go',
    java: 'Java',
    'c++': 'C++',
    cpp: 'C++',
    'c#': 'C#',
    csharp: 'C#',
    cs: 'C#',
    html: 'HTML',
    css: 'CSS',
    scss: 'SCSS',
    less: 'LESS',
    json: 'JSON',
    xml: 'XML',
    yaml: 'YAML',
    yml: 'YAML',
    vue: 'Vue',
    react: 'React',
    jsx: 'JSX',
    tsx: 'TSX',
    shell: 'Shell',
    bash: 'Bash',
    sh: 'Shell',
    sql: 'SQL',
    mysql: 'MySQL',
    markdown: 'Markdown',
    md: 'Markdown',
    php: 'PHP',
    ruby: 'Ruby',
    rb: 'Ruby',
    kotlin: 'Kotlin',
    kt: 'Kotlin',
    swift: 'Swift'
  }
  const dotColor = dotColors[l] || '#888'
  const displayName = displayNames[l] || (lang || 'Text')
  return '<span class="lang-badge">' +
    '<span class="lang-dot" style="background:' + dotColor + '"></span>' +
    '<span class="lang-name">' + displayName + '</span>' +
    '</span>'
}

/**
 * 轻量级 Markdown 渲染器
 * 支持：代码块、行内代码、标题、粗体、斜体、列表、链接、引用、表格、分隔线
 */
function renderMarkdown(text) {
  if (!text) return ''

  // 1. 先转义 HTML
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

  // 2. 提取代码块（```lang ... ```），用占位符替换
  const codeBlocks = []
  html = html.replace(/```(\w*)\n?([\s\S]*?)```/g, (match, lang, code) => {
    const idx = codeBlocks.length
    const rawCode = code.replace(/\n$/, '')
    // 反转义得到原始代码（用于运行/复制/下载功能）
    const originalCode = rawCode
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
    // 代码分出行，用于行号显示
    const lines = rawCode.split('\n')
    // 语法高亮
    const highlighted = highlightSyntax(rawCode, lang)
    const hlLines = highlighted.split('\n')
    codeBlocks.push({
      lang: lang || 'text',
      code: rawCode,
      originalCode: originalCode,
      lines: lines,
      hlLines: hlLines,
      lineCount: lines.length
    })
    return '\u0000CB' + idx + '\u0000'
  })

  // 3. 提取行内代码（`code`）
  const inlineCodes = []
  html = html.replace(/`([^`\n]+)`/g, (match, code) => {
    const idx = inlineCodes.length
    inlineCodes.push(code)
    return '\u0000IC' + idx + '\u0000'
  })

  // 4. 标题
  html = html.replace(/^###### (.+)$/gm, '<h6>$1</h6>')
  html = html.replace(/^##### (.+)$/gm, '<h5>$1</h5>')
  html = html.replace(/^#### (.+)$/gm, '<h4>$1</h4>')
  html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>')
  html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>')
  html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>')

  // 5. 分隔线
  html = html.replace(/^---+$/gm, '<hr>')

  // 6. 引用块
  html = html.replace(/^&gt; (.+)$/gm, '<blockquote>$1</blockquote>')
  // 合并连续引用
  html = html.replace(/(<blockquote>.*<\/blockquote>\n?)+/g, m => {
    const content = m.replace(/<\/?blockquote>/g, '').replace(/\n/g, '<br>')
    return '<blockquote>' + content + '</blockquote>'
  })

  // 7. 表格（简单支持）
  html = html.replace(
    /^\|(.+)\|\n\|([\s\-:|]+)\|\n((?:\|.+\|\n?)+)/gm,
    (match, header, sep, body) => {
      const headers = header.split('|').map(h => h.trim()).filter(Boolean)
      const rows = body.trim().split('\n').map(row =>
        row.split('|').map(c => c.trim()).filter(Boolean)
      )
      let table = '<table><thead><tr>'
      headers.forEach(h => { table += '<th>' + h + '</th>' })
      table += '</tr></thead><tbody>'
      rows.forEach(row => {
        table += '<tr>'
        headers.forEach((_, i) => { table += '<td>' + (row[i] || '') + '</td>' })
        table += '</tr>'
      })
      table += '</tbody></table>'
      return table
    }
  )

  // 8. 无序列表
  html = html.replace(/^[\-\*] (.+)$/gm, '<li>$1</li>')
  html = html.replace(/(?:<li>.*<\/li>\n?)+/g, m => '<ul>' + m + '</ul>')

  // 9. 有序列表
  html = html.replace(/^\d+\. (.+)$/gm, '<li class="ol-item">$1</li>')
  html = html.replace(/(?:<li class="ol-item">.*<\/li>\n?)+/g, m => '<ol>' + m.replace(/ class="ol-item"/g, '') + '</ol>')

  // 10. 粗体、斜体、删除线
  html = html.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
  html = html.replace(/__(.+?)__/g, '<strong>$1</strong>')
  html = html.replace(/\*([^*]+?)\*/g, '<em>$1</em>')
  html = html.replace(/~~(.+?)~~/g, '<del>$1</del>')

  // 11. 图片判断工具
  const IMAGE_EXT = /\.(jpg|jpeg|png|gif|webp|bmp|svg|ico|tiff?|avif)(\?.*)?(#.*)?$/i
  function isImageUrl(url) {
    return IMAGE_EXT.test(url)
  }

  // 11.1 Markdown 图片 ![alt](url) — 直接展示为图片
  html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (match, alt, url) => {
    // 反转义 URL
    const realUrl = url.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
    return '<figure class="inline-image-figure"><img class="inline-image" src="' + realUrl +
      '" alt="' + (alt || '图片') + '" loading="lazy" onclick="window.open(this.src, \'_blank\')"></figure>'
  })

  // 11. 链接（带图片链接悬浮预览）
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, text, url) => {
    const realUrl = url.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
    if (isImageUrl(realUrl)) {
      // 图片链接：加悬浮预览
      return '<a href="' + realUrl + '" target="_blank" rel="noopener" class="image-link">' +
        text +
        '<span class="image-hover-preview"><img src="' + realUrl + '" loading="lazy"></span>' +
        '</a>'
    }
    return '<a href="' + realUrl + '" target="_blank" rel="noopener">' + text + '</a>'
  })

  // 11.2 裸图片 URL（未被包裹为链接的独立图片网址，行级）
  // 在还原代码块、行内代码之后再处理，避免影响代码内部内容
  // 这里先占位，后面处理

  // 12. 换行处理：块级元素之间不额外加 br
  html = html.replace(/\n/g, '<br>')
  // 修复块级元素前后的多余 br
  html = html.replace(/<br>(<(?:h[1-6]|ul|ol|li|blockquote|hr|table|pre|div))/g, '$1')
  html = html.replace(/(<\/(?:h[1-6]|ul|ol|li|blockquote|hr|table|pre|div)>)<br>/g, '$1')

  // 13. 还原行内代码
  inlineCodes.forEach((code, i) => {
    html = html.replace('\u0000IC' + i + '\u0000', '<code class="inline-code">' + code + '</code>')
  })

  // 14. 还原代码块（带行号、语言徽章、复制/展开/下载按钮，HTML额外加运行按钮）
  // 初始化全局代码存储（供复制/下载/运行功能使用原始代码）
  window.__codeStore = window.__codeStore || {}
  codeBlocks.forEach((block, i) => {
    const codeId = 'code-' + Date.now() + '-' + i
    // 存储原始代码（保留换行、未转义），供运行/复制/下载使用
    window.__codeStore[codeId] = block.originalCode
    const isHtml = /^html?$/i.test(block.lang)
    // 生成行号列 HTML
    let lineNumbersHtml = ''
    for (let n = 1; n <= block.lineCount; n++) {
      lineNumbersHtml += '<div class="code-line-num">' + n + '</div>'
    }
    // 生成高亮后的代码行 HTML
    let codeLinesHtml = ''
    block.hlLines.forEach((line, n) => {
      // 空行用空格占位，保持高度
      codeLinesHtml += '<div class="code-line">' + (line || '&nbsp;') + '</div>'
    })
    // HTML运行按钮（仅HTML代码显示）
    const runBtn = isHtml
      ? '<button class="code-run-btn" onclick="runHtmlCode(\'' + codeId + '\')" title="运行预览 HTML">' +
        '<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><polygon points="6,4 20,12 6,20"/></svg>' +
        '<span>运行</span>' +
        '</button>'
      : ''
    const codeBlockHtml =
      '<div class="code-block">' +
      // 顶部工具栏
      '<div class="code-header">' +
      // 左侧：语言徽章 + HTML运行按钮
      '<div class="code-header-left">' +
      getLangBadge(block.lang) +
      runBtn +
      '</div>' +
      // 右侧：三个操作按钮
      '<div class="code-actions">' +
      // 复制按钮
      '<button class="code-action-btn" onclick="copyCode(\'' + codeId + '\')" title="复制代码">' +
      '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>' +
      '</button>' +
      // 展开/全屏按钮
      '<button class="code-action-btn" onclick="toggleCodeFullscreen(this)" title="全屏查看">' +
      '<svg class="icon-expand" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 3 21 3 21 9"></polyline><polyline points="9 21 3 21 3 15"></polyline><line x1="21" y1="3" x2="14" y2="10"></line><line x1="3" y1="21" x2="10" y2="14"></line></svg>' +
      '<svg class="icon-collapse" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:none"><polyline points="4 14 10 14 10 20"></polyline><polyline points="20 10 14 10 14 4"></polyline><line x1="14" y1="10" x2="21" y2="3"></line><line x1="3" y1="21" x2="10" y2="14"></line></svg>' +
      '</button>' +
      // 下载按钮
      '<button class="code-action-btn" onclick="downloadCode(\'' + codeId + '\',\'' + block.lang + '\')" title="下载代码">' +
      '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>' +
      '</button>' +
      '</div>' +
      '</div>' +
      // 代码区：左侧行号 + 右侧代码内容
      '<div class="code-body">' +
      '<div class="code-gutter">' + lineNumbersHtml + '</div>' +
      '<pre class="code-content"><code id="' + codeId + '" class="language-' + block.lang + '">' + codeLinesHtml + '</code></pre>' +
      '</div>' +
      '</div>'
    html = html.replace('\u0000CB' + i + '\u0000', codeBlockHtml)
  })

  // 15. 处理裸图片 URL（未被链接/代码块包裹的图片地址）
  // 匹配 https?:// 开头，图片扩展名结尾的 URL
  const bareImageRegex = /(^|[\s>;])(https?:\/\/[^\s<>"']+\.(jpg|jpeg|png|gif|webp|bmp|svg|ico|tiff?|avif)(\?[^\s<>"']*)?(#[^\s<>"']*)?)/gi
  html = html.replace(bareImageRegex, (match, prefix, url) => {
    const imgHtml = '<figure class="inline-image-figure"><img class="inline-image" src="' + url +
      '" alt="图片" loading="lazy" onclick="window.open(this.src, \'_blank\')"></figure>'
    return prefix + imgHtml
  })

  // 16. 处理裸非图片 URL（普通网址）转为可点击链接（已经在上面处理过，这里只处理未被识别的纯文本URL）
  const bareUrlRegex = /(^|[\s>;])(https?:\/\/[^\s<>"']+)/gi
  html = html.replace(bareUrlRegex, (match, prefix, url) => {
    // 跳过已经是 <a> 或 <img> 标签属性里的 URL
    if (prefix && (prefix.endsWith('href="') || prefix.endsWith('src="'))) return match
    const safeUrl = url.replace(/"/g, '&quot;')
    const isImg = /\.(jpg|jpeg|png|gif|webp|bmp|svg|ico|tiff?|avif)(\?.*)?(#.*)?$/i.test(url)
    if (isImg) return prefix + '<a href="' + safeUrl + '" target="_blank" rel="noopener" class="image-link">' + url +
      '<span class="image-hover-preview"><img src="' + safeUrl + '" loading="lazy"></span></a>'
    return prefix + '<a href="' + safeUrl + '" target="_blank" rel="noopener">' + url + '</a>'
  })

  return html
}

// 全局复制代码函数
window.copyCode = function(codeId) {
  // 优先从全局存储获取原始代码（保留换行、未转义）
  const text = (window.__codeStore && window.__codeStore[codeId]) ||
    (document.getElementById(codeId) ? document.getElementById(codeId).textContent : '')
  if (!text) return
  // 找到复制按钮（是工具栏第一个按钮）
  const codeBlock = document.getElementById(codeId).closest('.code-block')
  const btn = codeBlock ? codeBlock.querySelectorAll('.code-action-btn')[0] : null
  copyToClipboard(text).then(() => {
    if (btn) {
      const original = btn.innerHTML
      btn.innerHTML = '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="#a6e3a1" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>'
      btn.style.color = '#a6e3a1'
      setTimeout(() => {
        btn.innerHTML = original
        btn.style.color = ''
      }, 2000)
    }
  })
}

// 代码块全屏/退出全屏切换
window.toggleCodeFullscreen = function(btn) {
  const codeBlock = btn.closest('.code-block')
  if (!codeBlock) return
  const iconExpand = btn.querySelector('.icon-expand')
  const iconCollapse = btn.querySelector('.icon-collapse')
  if (!codeBlock.classList.contains('code-fullscreen')) {
    // 进入全屏
    codeBlock.classList.add('code-fullscreen')
    if (iconExpand) iconExpand.style.display = 'none'
    if (iconCollapse) iconCollapse.style.display = 'inline-block'
  } else {
    // 退出全屏
    codeBlock.classList.remove('code-fullscreen')
    if (iconExpand) iconExpand.style.display = 'inline-block'
    if (iconCollapse) iconCollapse.style.display = 'none'
  }
}

// 下载代码为文件
window.downloadCode = function(codeId, lang) {
  // 优先从全局存储获取原始代码
  const text = (window.__codeStore && window.__codeStore[codeId]) ||
    (document.getElementById(codeId) ? document.getElementById(codeId).textContent : '')
  if (!text) return
  // 根据语言推断后缀
  const extMap = {
    javascript: 'js', js: 'js', typescript: 'ts', ts: 'ts',
    python: 'py', py: 'py', java: 'java', go: 'go', golang: 'go',
    rust: 'rs', rs: 'rs', 'c++': 'cpp', cpp: 'cpp', 'c#': 'cs',
    csharp: 'cs', cs: 'cs', c: 'c', html: 'html', css: 'css',
    scss: 'scss', less: 'less', json: 'json', xml: 'xml',
    yaml: 'yaml', yml: 'yml', shell: 'sh', bash: 'sh', sh: 'sh',
    sql: 'sql', mysql: 'sql', markdown: 'md', md: 'md',
    vue: 'vue', jsx: 'jsx', tsx: 'tsx', php: 'php',
    ruby: 'rb', rb: 'rb', kotlin: 'kt', kt: 'kt', swift: 'swift'
  }
  const ext = extMap[(lang || '').toLowerCase()] || lang || 'txt'
  const filename = 'code-' + Date.now() + '.' + ext
  // 创建 blob 并下载
  try {
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  } catch (e) {
    // 回退方案
    copyToClipboard(text)
    alert('当前浏览器不支持下载，代码已复制到剪贴板')
  }
}

// 运行 HTML 代码（右侧抽屉预览，代码+预览并排）
window.runHtmlCode = function(codeId) {
  // 优先从全局存储获取原始代码（保留换行、未转义）
  const html = (window.__codeStore && window.__codeStore[codeId]) ||
    (document.getElementById(codeId) ? document.getElementById(codeId).textContent : '')
  if (!html) return
  // 如果当前已有预览，先关闭（复用或移除）
  const existing = document.getElementById('html-preview-drawer')
  if (existing) existing.remove()
  const mask = document.getElementById('html-preview-mask')
  if (mask) mask.remove()

  // 遮罩层（覆盖在主内容上，点击关闭）
  const maskEl = document.createElement('div')
  maskEl.id = 'html-preview-mask'
  maskEl.className = 'html-preview-mask'
  document.body.appendChild(maskEl)
  maskEl.addEventListener('click', closeHtmlPreview)

  // 抽屉面板
  const drawer = document.createElement('div')
  drawer.id = 'html-preview-drawer'
  drawer.className = 'html-preview-drawer'
  drawer.innerHTML =
    '  <div class="drawer-header">' +
    '    <div class="drawer-title">' +
    '      <svg class="drawer-title-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>' +
    '      <span id="html-preview-title-text">HTML 实时预览</span>' +
    '    </div>' +
    '    <div class="drawer-tools">' +
    '      <button class="drawer-tool-btn" onclick="reloadHtmlPreview()" title="重新运行">' +
    '        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"></polyline><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path></svg>' +
    '        <span>重新运行</span>' +
    '      </button>' +
    '      <button class="drawer-tool-btn drawer-tool-btn-primary" onclick="openHtmlInNewTab()" title="新窗口打开">' +
    '        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>' +
    '        <span>新窗口</span>' +
    '      </button>' +
    '      <button class="drawer-close" onclick="closeHtmlPreview()" title="关闭">' +
    '        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>' +
    '      </button>' +
    '    </div>' +
    '  </div>' +
    '  <div class="drawer-body">' +
    '    <iframe id="html-preview-iframe" class="drawer-iframe" frameborder="0"></iframe>' +
    '  </div>'
  document.body.appendChild(drawer)

  // 保存代码到全局
  window.__lastHtmlCode = html

  // 触发滑入动画
  requestAnimationFrame(function() {
    drawer.classList.add('drawer-open')
    maskEl.classList.add('mask-visible')
    loadHtmlToIframe(html)
  })

  // ESC 关闭
  drawer._escHandler = function(e) {
    if (e.key === 'Escape') closeHtmlPreview()
  }
  document.addEventListener('keydown', drawer._escHandler)
}

window.loadHtmlToIframe = function(html) {
  const iframe = document.getElementById('html-preview-iframe')
  if (!iframe) return
  // 使用 Blob URL 注入，比 srcdoc 兼容性更好，能正确处理外部资源
  // 清理旧的 Blob URL
  if (iframe._blobUrl) {
    URL.revokeObjectURL(iframe._blobUrl)
  }
  var blob = new Blob([html], { type: 'text/html;charset=utf-8' })
  var url = URL.createObjectURL(blob)
  iframe._blobUrl = url
  iframe.src = url
}

window.reloadHtmlPreview = function() {
  if (window.__lastHtmlCode) loadHtmlToIframe(window.__lastHtmlCode)
}

window.openHtmlInNewTab = function() {
  if (!window.__lastHtmlCode) return
  const blob = new Blob([window.__lastHtmlCode], { type: 'text/html;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  window.open(url, '_blank')
  setTimeout(function() { URL.revokeObjectURL(url) }, 5000)
}

window.closeHtmlPreview = function() {
  const drawer = document.getElementById('html-preview-drawer')
  const mask = document.getElementById('html-preview-mask')
  if (drawer && drawer._escHandler) {
    document.removeEventListener('keydown', drawer._escHandler)
  }
  if (drawer) {
    drawer.classList.remove('drawer-open')
    setTimeout(function() { drawer.remove() }, 260)
  }
  if (mask) {
    mask.classList.remove('mask-visible')
    setTimeout(function() { mask.remove() }, 260)
  }
  window.__lastHtmlCode = null
}

function copyToClipboard(text) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text)
  }
  return new Promise((resolve) => {
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.select()
    try { document.execCommand('copy') } catch (e) {}
    document.body.removeChild(textarea)
    resolve()
  })
}

export default {
  name: 'ChatMessage',
  props: {
    message: { type: Object, required: true }
  },
  data() {
    return {
      copied: false
    }
  },
  computed: {
    renderedContent() {
      if (this.message.role === 'assistant') {
        return renderMarkdown(this.message.content || '')
      }
      // 用户消息：简单转义 + 基础格式
      return this.message.content
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>')
    }
  },
  methods: {
    formatTime,
    formatFileSize,
    previewImage() {
      window.open(this.message.content, '_blank')
    },
    async handleCopy() {
      await copyToClipboard(this.message.content || '')
      this.copied = true
      setTimeout(() => { this.copied = false }, 2000)
    },
    handleRetry() {
      this.$emit('retry', this.message)
    },
    handleDelete() {
      this.$emit('delete', this.message)
    }
  }
}
</script>

<style scoped>
.message {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
}

.message.user {
  flex-direction: row-reverse;
}

.avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}

.message.user .avatar {
  background: #e6f7f5;
}

.bubble {
  max-width: 70%;
  min-width: 60px;
  padding: 12px 16px;
  border-radius: 12px;
  background: #f5f5f5;
  position: relative;
  box-sizing: border-box;
}

.message.user .bubble {
  background: #1a9c8e;
  color: #fff;
}

.message.user .bubble .msg-time {
  color: rgba(255, 255, 255, 0.7);
}

.msg-text {
  font-size: 14px;
  line-height: 1.7;
  word-break: break-word;
  overflow-wrap: break-word;
  width: 100%;
}

.msg-text ::v-deep(*) {
  max-width: 100%;
}

.msg-text ::v-deep(pre),
.msg-text ::v-deep(.code-block),
.msg-text ::v-deep(table) {
  width: 100%;
  box-sizing: border-box;
}

.msg-text ::v-deep(h1) { font-size: 1.5em; font-weight: 700; margin: 0.6em 0 0.4em; }
.msg-text ::v-deep(h2) { font-size: 1.3em; font-weight: 700; margin: 0.6em 0 0.4em; }
.msg-text ::v-deep(h3) { font-size: 1.15em; font-weight: 600; margin: 0.5em 0 0.3em; }
.msg-text ::v-deep(h4) { font-size: 1em; font-weight: 600; margin: 0.5em 0 0.3em; }
.msg-text ::v-deep(h5) { font-size: 0.9em; font-weight: 600; margin: 0.4em 0 0.3em; }
.msg-text ::v-deep(h6) { font-size: 0.85em; font-weight: 600; margin: 0.4em 0 0.3em; color: #888; }

.msg-text ::v-deep(p) { margin: 0.3em 0; }

.msg-text ::v-deep(strong) { font-weight: 700; }
.msg-text ::v-deep(em) { font-style: italic; }
.msg-text ::v-deep(del) { text-decoration: line-through; }

.msg-text ::v-deep(a) {
  color: #1a9c8e;
  text-decoration: underline;
}
.message.user .msg-text ::v-deep(a) {
  color: #b3f0e8;
}

/* ===== 直接展示的图片（markdown图片 + 裸图片URL） ===== */
.msg-text ::v-deep(.inline-image-figure) {
  margin: 10px 0;
  display: inline-block;
  max-width: 100%;
}
.msg-text ::v-deep(.inline-image) {
  max-width: 100%;
  max-height: 360px;
  border-radius: 10px;
  cursor: zoom-in;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  display: block;
  object-fit: contain;
  background: #f8f8f8;
  transition: transform 0.2s ease;
}
.msg-text ::v-deep(.inline-image:hover) {
  transform: scale(1.01);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
}

/* ===== 图片链接：悬浮预览 ===== */
.msg-text ::v-deep(a.image-link) {
  position: relative;
  display: inline;
  color: #8b5cf6;
  text-decoration: none;
  border-bottom: 1px dashed #c4b5fd;
  transition: color 0.15s ease;
}
.msg-text ::v-deep(a.image-link:hover) {
  color: #6d28d9;
  border-bottom-color: #8b5cf6;
}
.msg-text ::v-deep(a.image-link .image-hover-preview) {
  visibility: hidden;
  opacity: 0;
  position: absolute;
  left: 0;
  bottom: calc(100% + 10px);
  z-index: 9999;
  padding: 6px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.18);
  border: 1px solid #e5e7eb;
  transition: opacity 0.18s ease, visibility 0.18s ease, transform 0.18s ease;
  transform: translateY(4px);
  min-width: 180px;
  max-width: 380px;
  pointer-events: none;
}
.msg-text ::v-deep(a.image-link:hover .image-hover-preview) {
  visibility: visible;
  opacity: 1;
  transform: translateY(0);
}
.msg-text ::v-deep(a.image-link .image-hover-preview img) {
  display: block;
  max-width: 100%;
  max-height: 320px;
  border-radius: 8px;
  object-fit: contain;
  background: #f8f8f8;
}

.msg-text ::v-deep(ul),
.msg-text ::v-deep(ol) {
  margin: 0.4em 0;
  padding-left: 1.5em;
}
.msg-text ::v-deep(li) {
  margin: 0.2em 0;
}

.msg-text ::v-deep(blockquote) {
  margin: 0.5em 0;
  padding: 0.4em 0.8em;
  border-left: 3px solid #1a9c8e;
  background: rgba(0, 0, 0, 0.03);
  border-radius: 0 6px 6px 0;
  color: #555;
}
.message.user .msg-text ::v-deep(blockquote) {
  border-left-color: rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
}

.msg-text ::v-deep(hr) {
  border: none;
  border-top: 1px solid #ddd;
  margin: 0.8em 0;
}

.msg-text ::v-deep(table) {
  border-collapse: collapse;
  margin: 0.5em 0;
  font-size: 0.9em;
  width: 100%;
  max-width: 100%;
  display: block;
  overflow-x: visible;
}
.msg-text ::v-deep(th),
.msg-text ::v-deep(td) {
  border: 1px solid #ddd;
  padding: 6px 12px;
  text-align: left;
}
.msg-text ::v-deep(th) {
  background: rgba(0, 0, 0, 0.04);
  font-weight: 600;
}
.message.user .msg-text ::v-deep(th) {
  background: rgba(255, 255, 255, 0.1);
}

/* 行内代码 */
.msg-text ::v-deep(.inline-code) {
  background: rgba(0, 0, 0, 0.06);
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'Fira Code', 'Consolas', 'Monaco', monospace;
  font-size: 0.88em;
  color: #c7254e;
}
.message.user .msg-text ::v-deep(.inline-code) {
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
}

/* ========== 代码块：仿 IDE 风格 ========== */
.msg-text ::v-deep(.code-block) {
  margin: 0.6em 0;
  border-radius: 10px;
  background: #282c34;
  border: 1px solid #3a3f4b;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  position: relative;
  z-index: 1;
}

/* 全屏模式 */
.msg-text ::v-deep(.code-block.code-fullscreen) {
  position: fixed !important;
  top: 5vh;
  left: 5vw;
  right: 5vw;
  bottom: 5vh;
  width: 90vw;
  height: 90vh;
  z-index: 9999;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
}
/* 全屏遮罩 */
.msg-text ::v-deep(.code-block.code-fullscreen::after) {
  content: '';
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.7);
  z-index: -1;
}

/* 顶部工具栏 */
.msg-text ::v-deep(.code-header) {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 14px;
  background: #21252b;
  border-bottom: 1px solid #3a3f4b;
  flex-shrink: 0;
}

/* 工具栏左侧：语言徽章 + 运行按钮 */
.msg-text ::v-deep(.code-header-left) {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

/* HTML运行按钮（绿色强调色） */
.msg-text ::v-deep(.code-run-btn) {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, #34c759 0%, #30b350 100%);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 1px 3px rgba(52, 199, 89, 0.35);
}
.msg-text ::v-deep(.code-run-btn:hover) {
  background: linear-gradient(135deg, #3fd866 0%, #34c759 100%);
  transform: translateY(-0.5px);
  box-shadow: 0 2px 6px rgba(52, 199, 89, 0.45);
}
.msg-text ::v-deep(.code-run-btn:active) {
  transform: translateY(0);
}
.msg-text ::v-deep(.code-run-btn svg) {
  display: inline-block;
  filter: drop-shadow(0 1px 0 rgba(0,0,0,0.1));
}

/* 语言标签（极简胶囊风格） */
.msg-text ::v-deep(.lang-badge) {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px 3px 8px;
  border-radius: 12px;
  font-family: 'SF Mono', 'JetBrains Mono', 'Fira Code', 'Consolas', monospace;
  font-size: 11px;
  font-weight: 500;
  color: #abb2bf;
  background: #3a3f4b;
  letter-spacing: 0.3px;
  line-height: 1.4;
}
.msg-text ::v-deep(.lang-dot) {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.1);
}
.msg-text ::v-deep(.lang-name) {
  font-size: 11px;
  letter-spacing: 0.3px;
}

/* 工具栏右侧操作按钮组 */
.msg-text ::v-deep(.code-actions) {
  display: flex;
  align-items: center;
  gap: 2px;
}
.msg-text ::v-deep(.code-action-btn) {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  color: #9da5b4;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.15s;
}
.msg-text ::v-deep(.code-action-btn:hover) {
  background: rgba(255, 255, 255, 0.08);
  color: #e6e6e6;
}
.msg-text ::v-deep(.code-action-btn svg) {
  display: inline-block;
}

/* 代码主体区（行号+代码） */
.msg-text ::v-deep(.code-body) {
  display: flex;
  overflow: visible;
  font-family: 'Fira Code', 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.7;
}

/* 行号列 */
.msg-text ::v-deep(.code-gutter) {
  flex-shrink: 0;
  padding: 14px 10px 14px 14px;
  background: #21252b;
  border-right: 1px solid #3a3f4b;
  color: #5c6370;
  text-align: right;
  user-select: none;
  font-size: 12px;
  line-height: 1.7;
  min-width: 20px;
}
.msg-text ::v-deep(.code-line-num) {
  font-variant-numeric: tabular-nums;
  font-family: inherit;
  line-height: 1.7;
  height: 1.7em;
}

/* 代码内容区 */
.msg-text ::v-deep(.code-content) {
  flex: 1;
  margin: 0;
  padding: 14px 16px;
  overflow-x: auto;
  background: #282c34;
  color: #abb2bf;
  border-radius: 0 0 10px 10px;
}
.msg-text ::v-deep(.code-block.code-fullscreen .code-content) {
  flex: 1;
  overflow: auto;
  border-radius: 0;
}

.msg-text ::v-deep(.code-line) {
  line-height: 1.7;
  white-space: pre;
  word-wrap: normal;
  font-family: inherit;
  min-height: 1.7em;
}
.msg-text ::v-deep(.code-content code) {
  display: block;
  font-family: inherit;
  color: #abb2bf;
}

/* ========== 语法高亮颜色（仿 VS Code One Dark 风格） */
.msg-text ::v-deep(.hl-keyword) {
  color: #c678dd;  /* 紫色 - 关键字 */
  font-weight: 500;
}
.msg-text ::v-deep(.hl-method) {
  color: #61afef;  /* 蓝色 - 方法名 */
}
.msg-text ::v-deep(.hl-string) {
  color: #98c379;  /* 绿色 - 字符串 */
}
.msg-text ::v-deep(.hl-comment) {
  color: #5c6370;  /* 灰色 - 注释 */
  font-style: italic;
}

/* 代码内容区滚动条 */
.msg-text ::v-deep(.code-content::-webkit-scrollbar) {
  height: 8px;
  width: 8px;
}
.msg-text ::v-deep(.code-content::-webkit-scrollbar-track) {
  background: #21252b;
}
.msg-text ::v-deep(.code-content::-webkit-scrollbar-thumb) {
  background: #4b5263;
  border-radius: 4px;
}
.msg-text ::v-deep(.code-content::-webkit-scrollbar-thumb:hover) {
  background: #5c6370;
}

/* 气泡底部操作栏 */
.bubble-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
  gap: 8px;
}

.message-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  opacity: 0.9;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 4px;
  color: #999;
  background: transparent;
  cursor: pointer;
  transition: all 0.15s;
  padding: 0;
}

.action-btn:hover {
  background: rgba(0, 0, 0, 0.05);
  color: #1a9c8e;
}

.message.user .action-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  color: #b3f0e8;
}

.action-btn.copied {
  color: #1a9c8e;
}

.message.user .action-btn.copied {
  color: #b3f0e8;
}

.msg-image {
  max-width: 240px;
  max-height: 180px;
  border-radius: 8px;
  cursor: pointer;
}

.msg-file {
  display: flex;
  align-items: center;
  gap: 10px;
}

.file-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.file-name {
  font-size: 14px;
  font-weight: 500;
}

.file-size {
  font-size: 12px;
  opacity: 0.7;
}

.msg-time {
  font-size: 11px;
  color: #999;
  flex-shrink: 0;
  white-space: nowrap;
}

/* ====== 移动端 ≤768px ====== */
@media (max-width: 768px) {
  .message {
    gap: 8px;
    margin-bottom: 14px;
  }

  .avatar {
    width: 30px;
    height: 30px;
    font-size: 16px;
  }

  .bubble {
    max-width: 80%;
    padding: 10px 12px;
  }

  .msg-text {
    font-size: 14px;
  }

  .msg-text ::v-deep(.code-block) {
    border-radius: 8px;
  }
  .msg-text ::v-deep(.code-header) {
    padding: 6px 10px;
  }
  .msg-text ::v-deep(.lang-badge) {
    padding: 2px 8px 2px 6px;
    font-size: 10px;
  }
  .msg-text ::v-deep(.lang-dot) {
    width: 6px;
    height: 6px;
  }
  .msg-text ::v-deep(.lang-name) {
    font-size: 10px;
  }
  .msg-text ::v-deep(.code-action-btn) {
    width: 24px;
    height: 24px;
  }
  .msg-text ::v-deep(.code-body) {
    font-size: 12px;
  }
  .msg-text ::v-deep(.code-gutter) {
    padding: 10px 8px 10px 10px;
    font-size: 11px;
    min-width: 18px;
  }
  .msg-text ::v-deep(.code-content) {
    padding: 10px 12px;
  }
  .msg-text ::v-deep(.code-block.code-fullscreen) {
    top: 2vh;
    left: 2vw;
    right: 2vw;
    bottom: 2vh;
    width: 96vw;
    height: 96vh;
  }

  .msg-text ::v-deep(.code-run-btn) {
    padding: 2px 7px;
    font-size: 10px;
    border-radius: 8px;
  }

  .msg-image {
    max-width: 200px;
    max-height: 150px;
  }

  .bubble-footer {
    margin-top: 6px;
  }

  .action-btn {
    width: 22px;
    height: 22px;
  }
}

/* ====== HTML 右侧抽屉预览（全局样式） ====== */
</style>
<style>
/* 遮罩层：左侧半透明，点击关闭 */
.html-preview-mask {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.25);
  z-index: 9998;
  opacity: 0;
  transition: opacity 0.26s ease;
  pointer-events: none;
}
.html-preview-mask.mask-visible {
  opacity: 1;
  pointer-events: auto;
}

/* 右侧抽屉主体 */
.html-preview-drawer {
  position: fixed;
  top: 0; right: 0; bottom: 0;
  width: 40%;
  min-width: 360px;
  max-width: 720px;
  background: #ffffff;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  box-shadow: -8px 0 32px rgba(0, 0, 0, 0.18);
  transform: translateX(100%);
  transition: transform 0.28s cubic-bezier(0.16, 1, 0.3, 1);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  /* 左侧分割线 */
  border-left: 1px solid #e5e7eb;
}

/* 滑入状态 */
.html-preview-drawer.drawer-open {
  transform: translateX(0);
}

/* 抽屉顶部标题栏 */
.drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 18px;
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
  gap: 12px;
}

.drawer-title {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.drawer-title-icon {
  color: #1a9c8e;
  flex-shrink: 0;
}
.drawer-title span {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.drawer-tools {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

/* 工具按钮 */
.drawer-tool-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 11px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #6b7280;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  font-family: inherit;
}
.drawer-tool-btn:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
  color: #374151;
}
.drawer-tool-btn-primary {
  color: #1a9c8e;
  border-color: rgba(26, 156, 142, 0.35);
  background: #f0faf8;
}
.drawer-tool-btn-primary:hover {
  background: #e1f5f1;
  border-color: #1a9c8e;
  color: #138878;
}

/* 关闭按钮 */
.drawer-close {
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  border-radius: 6px;
  color: #9ca3af;
  cursor: pointer;
  transition: all 0.15s;
  margin-left: 2px;
}
.drawer-close:hover {
  background: #fef2f2;
  color: #ef4444;
}

/* 抽屉主体：iframe 预览 */
.drawer-body {
  flex: 1;
  background: #fff;
  overflow: hidden;
  position: relative;
}
.drawer-iframe {
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
  background: #fff;
}

/* 分割线增强效果 */
.html-preview-drawer::before {
  content: '';
  position: absolute;
  top: 0; left: -1px;
  width: 3px;
  height: 100%;
  background: linear-gradient(180deg, transparent, rgba(26, 156, 142, 0.25), transparent);
  pointer-events: none;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .html-preview-drawer {
    width: 92vw;
    min-width: 0;
    max-width: none;
    border-left: none;
    box-shadow: -4px 0 24px rgba(0, 0, 0, 0.15);
  }
  .drawer-header {
    padding: 10px 12px;
  }
  .drawer-title span {
    font-size: 13px;
  }
  .drawer-tool-btn span {
    display: none;
  }
  .drawer-tool-btn {
    padding: 5px 7px;
  }
}
</style>
